import { useCustomerKey } from "utils/customHooks/useCustomerKey";
import {
  useCurrentShift,
  useFocusRange,
  useSelectedShift,
} from "utils/customHooks/genericHooks";
import {
  midgardClients,
  DATA_DUMP_AGGREGATE_QUERY,
} from "providers/MidgardProvider";
import useQueryWithErrorHandling from "utils/useQuery";
import { useProcessIdOnSingleProcessPage } from "components/CustomGridItems/LotInfoBar/lotInfoUtils";
import { ONE_HOUR_OF_MS } from "utils/timeUtils";
import { HUBLET_VIEW_DATA_DUMP_POLL_INTERVAL } from "constants/intervals";
import { DATA_DUMP_FOR_HUBLET_QUERY_TITLE } from "contexts/ErrorManager/errorManagerUtils";
import { updateHubletViewDataDump } from "providers/GlobalStateProvider";
import { useDispatch } from "react-redux";
import {
  cleanRawDataDumpRow,
  useCleanRawDataDump,
} from "segments/DataDumpGetter/dataDumpGetterUtils";
import { useGeForDataDumpQueryHubletView } from "./hubletViewUtils";
import { useIsTimeRangeSmallEnough } from "segments/DataDumpGetter/DataDumpGetter";
import { isEqual } from "lodash";
import useQueryString from "utils/customHooks/useQueryString";
import { useLastDataDumpRow } from "components/DataGetter/LastDataDumpRowGetter";
import { useEffect, useMemo, useRef, useState, useTransition } from "react";
import { useInterval } from "react-use";
import { useSelector } from "store/useSelector";
import { useFlightConfig } from "components/DataGetter/FlightConfig/useFlightConfig";
import { useSkuTimelineSegments } from "./SingleTimeline/singleTimelineStore";

/** Hublet View always shows *current shift* data only, does not query for past shifts */
export default function HubletViewDataDumpGetter() {
  const now = useRef(Date.now());
  useInterval(() => {
    now.current = Date.now();
  }, 1000);
  const processId = useProcessIdOnSingleProcessPage();

  const currentShift = useCurrentShift();

  const selectedShift = useSelectedShift();
  const selectedShiftStart = selectedShift.range.ge;
  const selectedShiftEnd = selectedShift.range.lt;

  const isFullDaySelected = useSelector(
    state => state.legacy.isFullDaySelected
  );
  const isCurrentShiftSelected =
    !isFullDaySelected && isEqual(currentShift, selectedShift);
  const focusRange = useFocusRange();

  const isCurrentDaySelected =
    isFullDaySelected &&
    focusRange &&
    focusRange.ge <= now.current &&
    now.current < focusRange.lt;
  const geForDataDumpQuery = useGeForDataDumpQueryHubletView();

  const parsedQuery = useQueryString();
  const isCustomRangeSelector = "ge" in parsedQuery && "lt" in parsedQuery;
  const isCurrent =
    !isCustomRangeSelector && (isCurrentDaySelected || isCurrentShiftSelected);

  const [
    {
      hubletView: { showCountsSinceShiftStartOnly },
    },
  ] = useFlightConfig();

  const skuTimelineSegments = useSkuTimelineSegments();
  const skuSegmentsInShift =
    processId && skuTimelineSegments[processId]
      ? skuTimelineSegments[processId]?.filter(
          segment =>
            selectedShiftStart <= segment.range.ge &&
            segment.range.lt &&
            segment.range.lt < selectedShiftEnd
        )
      : [];
  const lastSkuSegmentInShift =
    skuSegmentsInShift[skuSegmentsInShift.length - 1];
  const lastSkuStartTime = lastSkuSegmentInShift?.range.ge;

  const variables = useMemo(() => {
    const variables = {
      ge: String(lastSkuStartTime),
      lt: String(selectedShiftEnd),
      machemetryIds: processId ? [processId] : [],
    };
    if (isCustomRangeSelector) {
      variables.ge = focusRange
        ? String(focusRange.ge)
        : String(parsedQuery.ge);
      variables.lt = focusRange
        ? String(focusRange.lt)
        : String(parsedQuery.lt);
    } else if (isCurrent) {
      variables.ge = String(geForDataDumpQuery);
      variables.lt = String(
        Math.min(focusRange?.lt || selectedShiftEnd, now.current)
      );
    } else if (showCountsSinceShiftStartOnly) {
      variables.ge = String(selectedShiftStart);
      variables.lt = String(selectedShiftEnd);
    }
    return variables;
  }, [
    lastSkuStartTime,
    selectedShiftEnd,
    processId,
    isCustomRangeSelector,
    focusRange,
    parsedQuery,
    geForDataDumpQuery,
    showCountsSinceShiftStartOnly,
    isCurrent,
    selectedShiftStart,
  ]);

  const queryRangeDuration = Number(variables.lt) - Number(variables.ge);
  const isTimeRangeInverted = queryRangeDuration < 0;
  const isTimeRangeZero = queryRangeDuration === 0;
  if (isTimeRangeInverted) {
    console.error(
      `HubletView time range is inverted! location.pathname: ${
        window.location.pathname
      }, query range ${(queryRangeDuration / ONE_HOUR_OF_MS).toFixed(
        1
      )} hours, variables: ${JSON.stringify(variables)}}`
    );
  }

  const { isTimeRangeSmallEnough } =
    useIsTimeRangeSmallEnough(queryRangeDuration);

  const skip =
    isNaN(Number(variables.ge)) ||
    (isCurrent && Number(geForDataDumpQuery) === 0) ||
    isTimeRangeInverted ||
    isTimeRangeZero ||
    !processId ||
    !selectedShiftEnd ||
    !selectedShiftStart ||
    !isTimeRangeSmallEnough;

  const [completed, setCompleted] = useState(false);
  const [retryKey, setRetryKey] = useState(Math.random());
  useInterval(() => {
    if (!completed) {
      setRetryKey(Math.random());
    }
  }, 30 * 1000);

  return skip ? null : (
    <Query key={retryKey} variables={variables} setCompleted={setCompleted} />
  );
}

function Query({
  variables,
  setCompleted,
}: {
  variables: { ge: string; lt: string; machemetryIds: string[] };
  setCompleted;
}) {
  useEffect(() => {
    setCompleted(false);
  }, [variables, setCompleted]);

  const dispatch = useDispatch();
  const processId = useProcessIdOnSingleProcessPage();
  const ckey = useCustomerKey();
  const client = midgardClients.get(ckey);
  const cleanRawDataDump = useCleanRawDataDump();
  const [
    {
      general: { multiplyNetworkRequestIntervals },
    },
  ] = useFlightConfig();
  const [, setLastDataDumpRow] = useLastDataDumpRow();
  const [, startTransition] = useTransition();

  useQueryWithErrorHandling(
    DATA_DUMP_FOR_HUBLET_QUERY_TITLE,
    DATA_DUMP_AGGREGATE_QUERY,
    {
      client,
      variables,
      onError: err => {
        console.log(
          `HubletView data dump gone wrong: ${JSON.stringify(variables)}`
        );
        // useQueryWithErrorHandling will log the error + error code via handleError
      },
      onCompleted: data => {
        startTransition(() => {
          setCompleted(true);
          if (data?.dataDumpAggregate && processId) {
            const cleanedDataDump = cleanRawDataDump(data.dataDumpAggregate, [
              processId,
            ]);

            dispatch(updateHubletViewDataDump(cleanedDataDump));
            const lastDataDumpRow = data.dataDumpAggregate.lastSegment;
            const cleaned =
              lastDataDumpRow && cleanRawDataDumpRow(lastDataDumpRow);
            if (!cleaned) {
              console.log(
                "⚠ HubletViewDataDumpGetter: data.dataDumpAggregate.lastSegment not found"
              );
            }
            setLastDataDumpRow(cleaned);
          }
        });
      },
      pollInterval:
        HUBLET_VIEW_DATA_DUMP_POLL_INTERVAL * multiplyNetworkRequestIntervals,
    }
  );
  return null;
}
